import { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";

import type { FirmwareVersion } from "~/api/firmware/types";
import { SORT_DIRECTIONS } from "~/api/types";
import { FirmwareHeader } from "~/components/Firmware/FirmwareHeader";
import { FirmwareTable } from "~/components/Firmware/FirmwareTable";
import { ShowedModals } from "~/components/Firmware/ShowedModals";
import { PageWrapper } from "~/components/Shared/PageWrapper";
import { useFirmwareVersions } from "~/hooks/useFirmwares";
import { useDownloadFile } from "~/hooks/useGetFileManager";
import { useTableSearchParams } from "~/hooks/useTableSearchParams";
import { useFirmwareStore } from "~/stores";

interface LocationState {
  firmwareName?: string;
}

const PAGE_SIZE = 5;

export const FirmwareDetail = () => {
  const { id: firmwareId } = useParams();
  const location = useLocation();
  const { firmwareName } = (location.state as LocationState) ?? {};
  const {
    currentPage,
    setCurrentPage,
    search,
    setSearchText,
    sorting,
    setSorting,
    sortDirection,
    orderBy,
  } = useTableSearchParams();

  const firmwareIdNumber = Number(firmwareId);
  const { getFirmware } = useFirmwareStore();
  const storedFirmware = getFirmware(firmwareIdNumber);

  const { data: versions, isLoading: isLoadingVersions } = useFirmwareVersions(
    firmwareIdNumber,
    currentPage,
    PAGE_SIZE,
    search,
    orderBy ?? "created_at",
    sortDirection ?? SORT_DIRECTIONS.DESC,
  );

  const [modalOpen, setModalOpen] = useState<
    "upload" | "edit" | "delete" | null
  >(null);
  const [selectedVersion, setSelectedVersion] =
    useState<FirmwareVersion | null>(null);
  const [selectedFileId, setSelectedFileId] = useState<number | null>(null);
  const { data: downloadData, isDownloadingFile } = useDownloadFile(
    selectedFileId ?? 0,
  );

  useEffect(() => {
    if (downloadData?.temporary_url && selectedFileId) {
      window.open(downloadData.temporary_url, "_blank");
      setSelectedFileId(null);
    }
  }, [downloadData, selectedFileId]);

  const handleDownload = (fileId: number) => {
    setSelectedFileId(fileId);
  };

  // Get firmware name from Zustand store first, then fallback to location state
  const displayName = storedFirmware?.name ?? firmwareName ?? "";

  return (
    <PageWrapper>
      <FirmwareHeader
        title={displayName}
        badgeText={`${versions?.pagination?.total ?? 0} versions`}
        setSearchText={setSearchText}
        onUploadClick={() => setModalOpen("upload")}
      />
      <div className="mb-4 flex items-center gap-4"></div>
      <FirmwareTable
        data={versions?.data ?? []}
        pageSize={PAGE_SIZE}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        totalRows={versions?.pagination?.total ?? 0}
        isLoading={isLoadingVersions}
        isDownloadingFile={isDownloadingFile}
        selectedFileId={selectedFileId}
        onDownload={handleDownload}
        onEdit={(version) => {
          setSelectedVersion(version);
          setModalOpen("edit");
        }}
        onDelete={(version: FirmwareVersion) => {
          setSelectedVersion(version);
          setModalOpen("delete");
        }}
        sorting={sorting}
        setSorting={setSorting}
      />
      <ShowedModals
        modalOpen={modalOpen}
        setModalOpen={setModalOpen}
        selectedVersion={selectedVersion}
        firmwareId={firmwareId ? Number(firmwareId) : undefined}
      />
    </PageWrapper>
  );
};
